import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  Table as TableType,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { GenericTablePagination } from "./GenericTablePagination";

interface GenericTableProps<TData, TValue> {
  addRowClassNames?: (data: TData) => string | undefined;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  enableRowSelection?: boolean;
  onRowClick?: (data: TData) => void;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  rowSelection?: RowSelectionState;
  toolbarComponent: React.FC<{
    table: TableType<TData>;
  }>;
}

export function GenericTable<TData, TValue>({
  addRowClassNames,
  enableRowSelection = true,
  columns,
  data,
  onRowClick,
  onRowSelectionChange,
  rowSelection,
  toolbarComponent: Toolbar,
}: GenericTableProps<TData, TValue>) {
  const [genericSelection, setGenericSelection] = React.useState(
    !onRowSelectionChange && rowSelection ? rowSelection : {},
  ); // If onRowSelectionChange is provided, we don't manage selection state internally

  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState<string>("");
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection: onRowSelectionChange && rowSelection ? rowSelection : genericSelection, // Use external rowSelection if provided, otherwise use internal state
      columnFilters,
      globalFilter,
      pagination,
    },
    enableRowSelection,
    enableMultiSort: true,
    enableSortingRemoval: true,
    maxMultiSortColCount: 3,
    onRowSelectionChange:
      onRowSelectionChange && rowSelection ? onRowSelectionChange : setGenericSelection, // Use external onRowSelectionChange if provided, otherwise use internal state
    getRowId: (row: any) => row.id,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    autoResetPageIndex: false,
  });
  // Reset to first page when filters change and current page is out of bounds
  React.useEffect(() => {
    const pageCount = table.getPageCount();
    if (pagination.pageIndex >= pageCount && pageCount > 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [columnFilters, table, globalFilter, pagination.pageIndex, pagination.pageSize]);

  return (
    <div className="flex flex-col gap-4">
      <Toolbar table={table} />
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : (
                        <div
                          className={
                            header.column.getCanSort()
                              ? "cursor-pointer select-none flex items-center gap-1 hover:text-foreground/80"
                              : ""
                          }
                          onClick={header.column.getToggleSortingHandler()}
                          title={
                            header.column.getCanSort()
                              ? "Click to sort. Shift+Click for multi-column sort"
                              : undefined
                          }
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="inline-flex items-center gap-0.5">
                              {header.column.getIsSorted() === "asc" && (
                                <>
                                  <ArrowUp className="h-3.5 w-3.5" />
                                  {header.column.getSortIndex() > -1 && sorting.length > 1 && (
                                    <span className="text-xs font-medium">
                                      {header.column.getSortIndex() + 1}
                                    </span>
                                  )}
                                </>
                              )}
                              {header.column.getIsSorted() === "desc" && (
                                <>
                                  <ArrowDown className="h-3.5 w-3.5" />
                                  {header.column.getSortIndex() > -1 && sorting.length > 1 && (
                                    <span className="text-xs font-medium">
                                      {header.column.getSortIndex() + 1}
                                    </span>
                                  )}
                                </>
                              )}
                              {!header.column.getIsSorted() && (
                                <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/40" />
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={`${addRowClassNames ? addRowClassNames(row.original) : ""} ${onRowClick ? "cursor-pointer hover:bg-muted/50" : ""}`}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <GenericTablePagination enableRowSelection={enableRowSelection} table={table} />
    </div>
  );
}
